import { useMemo } from 'react';
import {
  usePatchTimesheets,
  usePatchTimesheetsTimesheetId,
  usePatchTimesheetsTimesheetIdEntries,
} from '../api/generated/arrayscapeGamingDashboardAPI';
import { Timesheet, TimesheetEntry, JsonPatchOperation } from '../api/generated/model';

// Adapters over the timesheet PATCH endpoints.
//
// The API used to expose a verb per action — submit, approve, reject, pay,
// unpay, unapprove, send-payment, batch-pay — plus a PUT that replaced a
// timesheet's entire entry list on every save. All of that is now JSON-PATCH:
// `replace /status` drives a transition, and entries are added, removed and
// edited one row at a time.
//
// These wrappers keep the call shapes the page already used, so the change
// stays in one place instead of spreading through every button handler.

export type LocalEntry = {
  activity_id: string;
  entry_date: string;
  hours_decimal?: number | null;
  hours_minutes?: string | null;
  description?: string;
};

const statusOp = (status: string): JsonPatchOperation => ({
  op: 'replace',
  path: '/status',
  value: status,
});

const fieldOp = (path: string, value: unknown): JsonPatchOperation => ({
  op: 'replace',
  path,
  value,
});

// entryKey is the grid's own notion of identity: one cell is one activity on
// one day. The server addresses entries by id, so the diff below pairs the
// two up on this key and reads the id off whichever server row matches.
const entryKey = (activityId: string, entryDate: string) => `${activityId}\u0000${entryDate}`;

const hoursOf = (e: LocalEntry): number => {
  if (e.hours_minutes) {
    const [h, m] = e.hours_minutes.split(':');
    return (parseInt(h, 10) || 0) + (parseInt(m, 10) || 0) / 60;
  }
  return e.hours_decimal ?? 0;
};

const sameHours = (local: LocalEntry, server: TimesheetEntry): boolean => {
  // Compare the canonical decimal rather than the typed representation: 3:30
  // and 3.5 are the same entry, and re-sending one as the other would mark
  // every row dirty on every save.
  return Math.abs(hoursOf(local) - (server.hours ?? 0)) < 1e-9;
};

/**
 * buildEntriesPatch diffs the grid's current state against what the server
 * holds and returns only the ops that actually change something.
 *
 * This is the point of moving off the replace-all PUT: that deleted every
 * entry and recreated it on each save, which destroyed edit history and made
 * a mistaken deletion unrecoverable. Here, deleting one row emits one remove.
 *
 * Entries with no hours and no description are treated as absent, because the
 * grid renders an empty cell for every activity on every day of the period —
 * without this, opening a timesheet and saving it would create hundreds of
 * blank rows.
 */
export function buildEntriesPatch(
  localEntries: LocalEntry[],
  serverEntries: TimesheetEntry[]
): JsonPatchOperation[] {
  const ops: JsonPatchOperation[] = [];

  const meaningful = (e: LocalEntry) =>
    e.activity_id !== '' && (hoursOf(e) > 0 || (e.description ?? '') !== '');

  const localByKey = new Map<string, LocalEntry>();
  for (const e of localEntries) {
    if (meaningful(e)) {
      localByKey.set(entryKey(e.activity_id, e.entry_date), e);
    }
  }

  const matchedKeys = new Set<string>();

  for (const server of serverEntries) {
    const key = entryKey(server.activity_id ?? '', server.entry_date ?? '');
    const local = localByKey.get(key);

    // Already paired with an earlier server row: the grid holds one entry per
    // activity per day, so a duplicate is stale and goes.
    if (!local || matchedKeys.has(key)) {
      if (server.id) {
        ops.push({ op: 'remove', path: `/${server.id}` });
      }
      continue;
    }
    matchedKeys.add(key);

    if (!server.id) continue;

    if (!sameHours(local, server)) {
      if (local.hours_minutes) {
        ops.push(fieldOp(`/${server.id}/hours_minutes`, local.hours_minutes));
      } else {
        ops.push(fieldOp(`/${server.id}/hours`, local.hours_decimal ?? 0));
      }
    }
    if ((local.description ?? '') !== (server.description ?? '')) {
      ops.push(fieldOp(`/${server.id}/description`, local.description ?? ''));
    }
  }

  for (const [key, local] of localByKey) {
    if (matchedKeys.has(key)) continue;
    ops.push({
      op: 'add',
      path: '/-',
      value: {
        activity_id: local.activity_id,
        entry_date: local.entry_date,
        ...(local.hours_minutes
          ? { hours_minutes: local.hours_minutes }
          : { hours: local.hours_decimal ?? 0 }),
        description: local.description ?? '',
      },
    });
  }

  return ops;
}

export function useTimesheetActions() {
  const patchEntries = usePatchTimesheetsTimesheetIdEntries();
  const patchTimesheet = usePatchTimesheetsTimesheetId();
  const patchMany = usePatchTimesheets();

  return useMemo(
    () => ({
      /** Save the grid: only what changed, plus notes if they did. */
      saveEntries: async (
        timesheetId: string,
        localEntries: LocalEntry[],
        serverEntries: TimesheetEntry[],
        notes: string,
        serverNotes: string
      ): Promise<void> => {
        const ops = buildEntriesPatch(localEntries, serverEntries);
        if (ops.length > 0) {
          await patchEntries.mutateAsync({ timesheetId, data: ops });
        }
        if (notes !== serverNotes) {
          await patchTimesheet.mutateAsync({
            timesheetId,
            data: [fieldOp('/notes', notes)],
          });
        }
      },

      setStatus: async (timesheetId: string, status: string, extra: JsonPatchOperation[] = []) => {
        await patchTimesheet.mutateAsync({
          timesheetId,
          // The status op and whatever it requires travel together, so the
          // server applies them as one atomic change.
          data: [statusOp(status), ...extra],
        });
      },

      /** Replaces batch-pay: one op set, one transaction. */
      payMany: async (timesheetIds: string[], paymentReference?: string) => {
        const data: JsonPatchOperation[] = [];
        for (const id of timesheetIds) {
          data.push({ op: 'replace', path: `/${id}/status`, value: 'paid' });
          if (paymentReference) {
            data.push({ op: 'replace', path: `/${id}/payment_reference`, value: paymentReference });
          }
        }
        await patchMany.mutateAsync({ data });
      },

      isPending: patchEntries.isPending || patchTimesheet.isPending || patchMany.isPending,
    }),
    [patchEntries, patchTimesheet, patchMany]
  );
}

export const rejectionOps = (reason: string): JsonPatchOperation[] => [
  fieldOp('/rejection_reason', reason),
];

export const paymentReferenceOps = (reference?: string): JsonPatchOperation[] =>
  reference ? [fieldOp('/payment_reference', reference)] : [];

export const sendPaymentOps = (externalMemo?: string, note?: string): JsonPatchOperation[] => {
  const ops: JsonPatchOperation[] = [];
  if (externalMemo) ops.push(fieldOp('/payment_external_memo', externalMemo));
  if (note) ops.push(fieldOp('/payment_note', note));
  return ops;
};

export type TimesheetActions = ReturnType<typeof useTimesheetActions>;
export type { Timesheet };
